import { Stack } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, CodeField, NumberField, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { useAbility } from 'providers/casl'
import { Schema } from '../../schema'
import { CommonProps } from '../../types'
import { AllowActivitiesRelatedSettings } from './AllowActivitiesRelatedSettings'
import { AuctionDelayRelatedSettings } from './AuctionDelayRelatedSettings'
import { KNOWLEDGE_BASE_URL } from './constants'
import { CustomPrebidConfigRelatedSettings } from './CustomPrebidConfigRelatedSettings'

export const AdvancedPrebidSection = ({
  workspacePrebidConfiguration,
}: CommonProps): React.JSX.Element => {
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.prebidStack.advancedPrebidSettings',
  })
  const { t: tC } = useTranslation('common')

  const { control, watch } = useFormContext<Schema>()
  const { customAuctionDelayEnabled, customizeAllowActivities, customPrebidConfigEnabled } = watch()

  const ability = useAbility()
  const cannotUpdate: boolean = ability.cannot('update', 'Domain')

  return (
    <Paper>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <Stack
        direction='row'
        spacing={2}
      >
        <BoxForm>
          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('customAuctionDelayEnabled.label')}
            labelPlacement='start'
            name='customAuctionDelayEnabled'
            tooltip={{
              content: t('customAuctionDelayEnabled.tooltip'),
              link: {
                title: tC('learnMore'),
                url: KNOWLEDGE_BASE_URL.AUCTION_DELAY,
              },
            }}
          />

          {customAuctionDelayEnabled && (
            <NumberField
              control={control}
              disabled={cannotUpdate}
              label={t('auctionDelay.label')}
              name='auctionDelay'
            />
          )}
        </BoxForm>

        {customAuctionDelayEnabled && workspacePrebidConfiguration && (
          <AuctionDelayRelatedSettings auctionDelay={workspacePrebidConfiguration.auctionDelay} />
        )}
      </Stack>

      <GapBox />

      <Stack
        direction='row'
        spacing={2}
      >
        <BoxForm>
          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('customizeAllowActivities.label')}
            labelPlacement='start'
            name='customizeAllowActivities'
            tooltip={{ content: t('customizeAllowActivities.tooltip') }}
          />
        </BoxForm>

        {customizeAllowActivities && workspacePrebidConfiguration && (
          <AllowActivitiesRelatedSettings
            allowActivitiesData={workspacePrebidConfiguration.allowActivitiesData}
          />
        )}
      </Stack>

      {customizeAllowActivities && (
        <BoxForm>
          <GapBox />

          <CodeField
            control={control}
            disabled={cannotUpdate}
            language='javascript'
            name='allowActivitiesData'
            options={{ renderValidationDecorations: 'off' }}
            title={t('allowActivitiesData.label')}
            tooltip={{
              content: t('allowActivitiesData.tooltip'),
              link: {
                title: tC('learnMore'),
                url: KNOWLEDGE_BASE_URL.ALLOW_ACTIVITIES,
              },
            }}
          />
        </BoxForm>
      )}

      <GapBox />

      <Stack
        direction='row'
        spacing={2}
      >
        <BoxForm>
          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('customPrebidConfigEnabled.label')}
            labelPlacement='start'
            name='customPrebidConfigEnabled'
            tooltip={{ content: t('customPrebidConfigEnabled.tooltip') }}
          />
        </BoxForm>

        {customPrebidConfigEnabled && workspacePrebidConfiguration && (
          <CustomPrebidConfigRelatedSettings
            prebidCustomConfig={workspacePrebidConfiguration.prebidCustomConfig}
          />
        )}
      </Stack>

      {customPrebidConfigEnabled && (
        <BoxForm>
          <GapBox />

          <CodeField
            control={control}
            disabled={cannotUpdate}
            language='json'
            name='prebidCustomConfig'
            title={t('prebidCustomConfig.label')}
            tooltip={{
              content: t('prebidCustomConfig.tooltip'),
            }}
          />
        </BoxForm>
      )}
    </Paper>
  )
}
